import { Col } from 'antd';
import { notification } from '../../../../utils/messaging';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toUTCDate } from '../../../../utils/date';
import moment from 'moment-timezone';
import AvailabilityCalendar, {
  emptyDatesObject,
} from '../../../availability_calendar/AvailabilityCalendar';
import '../../assets/css/live_availability_calendar.css';
import '../../../admin/components/bookings/assets/css/admin_booking.css';
import '../../../user_bookings/assets/css/user_bookings.css';
import { LiveAvailabilityTimes } from './LiveAvailabilityTimes';

/**
 * An AvailabilityCalendar component which automatically fetches the live availability
 * and selectable time values for a given lake (and optional package and booking type)
 */
const LiveAvailabilityCalendar = ({
  fisheryPublicId,
  lakeId = null,
  packageId = null,
  maxDate,
  showBookingTypeSelection,
  bookingType = '',
  apiErrorMessage,
}) => {
  const [bookingDates, setBookingDates] = useState([{ ...emptyDatesObject }]);
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);

  const [disabledDates, setDisabledDates] = useState([]);
  const [limitedDates, setLimitedDates] = useState([]);
  const [lakeDetails, setLakeDetails] = useState();

  const [currentMonth, setCurrentMonth] = useState(
    moment().format('YYYY-MM-DD')
  );
  const [arrivalTime, setArrivalTime] = useState();
  const [departureTime, setDepartureTime] = useState();
  const [availableTicket, setAvailableTicket] = useState('');

  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const [totalTickets, setTotalTickets] = useState('');

  const selectedPackageDetails = useMemo(
    () =>
      (lakeDetails?.custom_slot_days ?? []).find(({ id }) => id === packageId),
    [lakeDetails?.custom_slot_days, packageId]
  );
  const ADHfishery = 11797;
  const isSelectedPackageExclusive =
    selectedPackageDetails?.booking_type === 'lake_exclusive';

  const clearSelectedTimes = useCallback(() => {
    setArrivalTime(null);
    setDepartureTime(null);
  }, []);

  const clearSelectedDates = useCallback(() => {
    if (!bookingDates?.[0]) {
      return;
    }
    // Keeps the object reference invariant to prevent calendar re-render (weird bug with DateRangePicker)
    bookingDates[0] = { ...emptyDatesObject };
    setBookingDates(bookingDates);
    setArrivalDate(null);
    setDepartureDate(null);
    clearSelectedTimes();
  }, [bookingDates, clearSelectedTimes]);

  const getTicketAvailability = useCallback((payload_data) => {
    const axios_config = {
      headers: {
        Accept: 'application/json',

        'Content-Type': 'application/json',
      },
      withCredentials: true,
    };
    axios
      .post('booking/lake/swim_ticket/availability', payload_data, axios_config)
      .then((res) => {
        if (res.status === 200) {
          let _result = res.data.result;
          setAvailableTicket(_result.available_tickets);
          setTotalTickets(_result.total_tickets);
        } else {
          notification.error({
            description: 'Error processing result',
          });
        }
      })
      .catch((err) => {
        if (err && err.response) {
          if (err.response.status === 500) {
            notification.error({
              description: 'Server Response failed. Please try again later',
            });
          } else if (err.response.status === 401) {
            notification.error({
              description:
                'Your action will not be saved. Please Login and try again!',
            });
          } else if (err.response.status === 400) {
            notification.error({
              description: err.response.data['detail'],
            });
          } else if (err.response.status === 422) {
            if (err.response.data['detail'] === 'Signature has expired') {
              notification.error({
                description:
                  'Login session has been expired. Please Login to continue!',
              });
            } else {
              notification.error({
                description:
                  "OOPS! Server didn't like the request. If this persists please contact support team",
              });
            }
          } else if (err.response.status === 423) {
            notification.error({
              description:
                'Sorry ! this booking slot has been booked by another user, Try different booking!',
            });
          }
        }
      });
  }, []);
  useEffect(() => {
    setDisabledDates([]);
  }, [bookingType, lakeId]);

  useEffect(() => {
    if (arrivalTime && departureTime) {
      getTicketAvailability({
        arrival_timestamp: arrivalTime,
        departure_timestamp: departureTime,
        lake_id: lakeId,
        booking_type: bookingType ?? '',
      });
    }
  }, [arrivalTime, bookingType, departureTime, getTicketAvailability, lakeId]);

  const getBlockedBookingDatesForMonth = useCallback(
    (
      lake_id,
      _date,
      selectedPackageId = null,
      isSelectedPackageExclusive = false,
      userSelectedBookingType = ''
    ) => {
      setLoadingAvailability(true);

      const axios_config = {
        headers: {
          Accept: 'application/json',

          'Content-Type': 'application/json',
        },
        withCredentials: true,
      };

      let payload_data = {
        lake_id: lake_id,
        date: _date,
        ...(selectedPackageId
          ? {
              custom_slot_id: selectedPackageId,
              book_lake_exclusive: isSelectedPackageExclusive,
            }
          : {}),
        booking_type: userSelectedBookingType ?? '',
      };

      axios
        .post('booking/lake/details', payload_data, axios_config)
        .then((res) => {
          if (res.status === 200) {
            let _result = res.data.result;
            setLakeDetails(_result);
            setDisabledDates((disabledDates) => [
              ...disabledDates,
              ..._result.fully_booked_dates.map(toUTCDate),
            ]);
            setLimitedDates(
              _result?.limited_availability_dates?.map((ld) => ({
                ...ld,
                date: toUTCDate(ld.date),
              })) || []
            );
          } else {
            notification.error({
              description: 'Error processing result',
            });
          }
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status === 401) {
              notification.error({
                description: 'Please login to continue',
              });
            }
          } else {
            notification.error({
              description: 'Error processing result',
            });
          }
        })
        .finally(() => setLoadingAvailability(false));
    },
    [clearSelectedDates]
  );

  const handleMonthChange = useCallback((date) => {
    let _date = moment(date).format('YYYY-MM-DD');
    setCurrentMonth(_date);
    getBlockedBookingDatesForMonth(
      lakeId,
      _date,
      packageId,
      isSelectedPackageExclusive,
      bookingType
    );
  }, []);

  useEffect(() => {
    if (!currentMonth || !lakeId) {
      return;
    }
    clearSelectedDates();
    getBlockedBookingDatesForMonth(
      lakeId,
      currentMonth,
      packageId,
      isSelectedPackageExclusive,
      bookingType
    );
  }, [
    bookingType,
    getBlockedBookingDatesForMonth,
    isSelectedPackageExclusive,
    lakeId,
    packageId,
  ]);
  useEffect(() => {
    if (!currentMonth || !lakeId) {
      return;
    }

    getBlockedBookingDatesForMonth(
      lakeId,
      currentMonth,
      packageId,
      isSelectedPackageExclusive,
      bookingType
    );
  }, [currentMonth]);

  const handleDateChange = (selectedRange) => {
    // Reset timing
    setArrivalTime(null);
    setDepartureTime(null);
    setAvailableTicket('');
    setTotalTickets('');

    // Keeps array reference invariant to prevent calendar month reset
    bookingDates[0] = selectedRange;
    setBookingDates(bookingDates);
    setArrivalDate(toUTCDate(selectedRange.startDate));
    setDepartureDate(toUTCDate(selectedRange.endDate));
  };

  const getAvailabilityText = (lakeDetails) => {
    let bookingTypeTexts;
    if (
      lakeDetails?.lake_type?.toLowerCase() === 'ticket' ||
      (lakeDetails?.lake_type?.toLowerCase() === 'swim-ticket' &&
        bookingType === 'ticket')
    ) {
      bookingTypeTexts = 'Tickets';
    } else if (
      lakeDetails?.lake_type?.toLowerCase() === 'swim-ticket' &&
      bookingType === 'swim' &&
      fisheryPublicId === ADHfishery
    ) {
      bookingTypeTexts = 'Cabin';
    } else {
      bookingTypeTexts = 'Swims';
    }
    return `${bookingTypeTexts} Available `;
  };

  const needsBookingType = showBookingTypeSelection && !bookingType;
  const needsPackage = lakeDetails?.custom_slot_days && !packageId;
  const showOverlay =
    apiErrorMessage > '' || !lakeId || needsBookingType || needsPackage;

  const AvailabilityOverlay = ({ apiErrorMessage }) => (
    <div id='availability-overlay' className='br15'>
      {apiErrorMessage > '' ? (
        <div
          className='gBold center pd-10 bg-white fs-18 txt-center br20'
          style={{ width: '280px' }}
        >
          {apiErrorMessage}
        </div>
      ) : (
        <div
          className='gBold center pd-10 bg-white fs-18 txt-center br20'
          style={{ width: '280px' }}
        >
          Please select a{' '}
          {needsPackage
            ? 'package'
            : needsBookingType
              ? 'booking type'
              : 'lake'}{' '}
          above to see live availability
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className='live-availability-calendar gLight'>
        <div className='' key={'row-' + lakeDetails?.lake_id}>
          {showOverlay && (
            <AvailabilityOverlay apiErrorMessage={apiErrorMessage} />
          )}
          <AvailabilityCalendar
            key={'cal-' + lakeId + packageId}
            reRenderKey={lakeDetails?.lake_id + lakeId + packageId}
            selectedRanges={bookingDates}
            fullyBookedDates={disabledDates}
            limitedAvailabilityDates={limitedDates}
            loading={loadingAvailability}
            maxDate={maxDate}
            onDateSelection={handleDateChange}
            onMonthChange={handleMonthChange}
            clearSelectedDates={() => {
              clearSelectedDates();
              clearSelectedTimes();
            }}
            selectedPackage={selectedPackageDetails}
            showSelectionTail={lakeDetails?.is_24_hour_lake}
          />

          <LiveAvailabilityTimes
            fisheryPublicId={fisheryPublicId}
            selectedLakeId={lakeId}
            arrivalTime={arrivalTime}
            onChangeArrival={setArrivalTime}
            departureTime={departureTime}
            onChangeDeparture={setDepartureTime}
            arrivalDate={arrivalDate}
            departureDate={departureDate}
            lakeExclusive={
              selectedPackageDetails?.booking_type === 'lake_exclusive'
            }
            bookingType={bookingType}
          />

          <Col span={24} className='availabilty_container'>
            <p className='txt-center gSemiBold mg-bottom-0 fs-18'>
              {isSelectedPackageExclusive ? (
                'Lake Exclusive Booking: ' + (availableTicket && 'Available')
              ) : (
                <>
                  {getAvailabilityText(lakeDetails)}
                  {availableTicket && (
                    <span className='dark-green'>
                      {`${availableTicket} out of ${totalTickets}`}
                    </span>
                  )}
                </>
              )}
            </p>
            <p className='txt-center gRegular mg-bottom-0'>
              (Please select the dates & times you would like to fish above to
              see live availability)
            </p>
          </Col>
        </div>
      </div>
    </>
  );
};

export default LiveAvailabilityCalendar;
